/* istanbul ignore file */
import React, { useCallback, useEffect, useRef, useState } from 'react'
// @ts-ignore no types
import { connect } from 'react-redux'
// @ts-ignore no types
import _ from 'lodash'
import {
  Carousel,
  Icon,
  useApplicationContext,
  hooks,
  // @ts-ignore no types
} from '@adc/ui-adc'
import * as blueprint from '@adc/blueprint'
import type { Maybe } from '@adc/sdk/types'
import classnames from 'classnames/bind'
import { usePropertyContext } from '../../../context/property'
import { withModalHooks } from '../../../hooks/useModalFeatures'
import { useContactModal } from '../../../modules/propertyDetails/useContactModal'
import { BadgeViewType, mapBadgesToView } from '../../../utils/badges'
import { useListingContext } from '../../../context/listing'
import SectionPanel from '../../../components/sectionPanel'
import { NOTIFY_RESERVE } from '../../../statics'
// @ts-ignore no types
import cls from './styles.scss'

const cx = classnames.bind(cls)

export interface SavePropertyPayload {
  propertyBody: {
    global_property_id?: string
    property_id: Maybe<string>
    property_morpheus_id: string // primary_property_id
    property_address: string // formatted_address
    auction_id: string // event.event_id
  }
  source: string
  pushAuthQueueItem: (item: any) => void
  toggleAuthModal: () => void
  pushOnboardingQueueItem: (item: any) => void
  nudgeOptInModal?: () => void
}

const CARD_HEIGHT = 160

const BadgeCard = ({ badge }: { badge: BadgeViewType }) => {
  const [isOverflow, setIsOverflow] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const elmId = _.snakeCase(badge.title || badge.summary)
  const cardRef = useRef<HTMLDivElement>(null)

  const checkOverflow = () => {
    if (cardRef.current) {
      const isOverflowing = cardRef.current?.scrollHeight > CARD_HEIGHT
      if (isOverflowing) {
        !isOverflow && setIsOverflow(true)
      } else {
        isOverflow && setIsOverflow(false)
      }
    }
  }

  useEffect(() => {
    window.addEventListener('resize', checkOverflow)
    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [])

  useEffect(() => {
    checkOverflow()
  }, [badge, checkOverflow])

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <div
        className={cx('badge-card')}
        ref={cardRef}
        style={{
          minHeight: CARD_HEIGHT,
          maxHeight: !isExpanded ? CARD_HEIGHT : 1000,
        }}
      >
        {isOverflow && !isExpanded && (
          <div className={cx('overflow-layout')}>
            <a onClick={() => setIsExpanded(true)}>View More</a>
          </div>
        )}

        <div className={cx('badge-title')}>
          <Icon
            iconStyle='light'
            className={cx('hoisted-badge-icon')}
            name={badge.icon}
            data-elm-id={`${elmId}_icon`}
          />
          {badge.title}
        </div>
        <div className={cx('badge-description')}>{badge.content}</div>
      </div>
      {isExpanded && (
        <div className={cx('collapse-footer')}>
          <a onClick={() => setIsExpanded(false)}>View Less</a>
        </div>
      )}
    </div>
  )
}

export interface ImportantInformationSectionProps {
  toggleAuthModal: () => void
  pushOnboardingQueueItem: (item: any) => void
  pushAuthQueueItem: (item: any) => void
}

const ImportantInformationSection = ({
  toggleAuthModal,
  pushOnboardingQueueItem,
  pushAuthQueueItem,
}: ImportantInformationSectionProps) => {
  const { listing } = useListingContext()
  const { user } = useApplicationContext()
  const contactModal = useContactModal({ user })

  const openContactModal = () => {
    setTimeout(() => contactModal.open(), 10)
  }

  const toggleContactModalIfLogged = () => {
    if (user.logged_in) {
      openContactModal()
    } else {
      toggleAuthModal()
      pushOnboardingQueueItem([openContactModal])
    }
  }
  const { propertyActions } = usePropertyContext()
  const saveProperty = (args: any) =>
    propertyActions?.toggleSaveProperty({
      ...args,
      pushAuthQueueItem,
      toggleAuthModal,
    })

  const badgeActions = {
    saveProperty,
    toggleContactModal: toggleContactModalIfLogged,
  }
  const badgeList = blueprint.listing.utils.calculatePDPRedesignBadges(listing)

  const elmId = 'important_information'

  const badgeEventSubscriber = useCallback(
    ref => {
      const propertyBody = {
        global_property_id: listing.mlh_global_property_id,
        property_id: listing.mlh_property_id,
        property_morpheus_id: listing.primary_property.property_id,
        property_address: listing.formatted_address?.join(', '),
        auction_id: listing.event?.event_id,
      }
      if (ref === 'contact_agent' || ref === 'interior_access_contact_agent') {
        badgeActions.toggleContactModal()
      }

      if (ref === 'notify_me') {
        badgeActions.saveProperty({
          property: propertyBody,
          listing,
          source: NOTIFY_RESERVE,
        })
      }
    },
    [listing, badgeActions]
  )
  const badgesWithContent = mapBadgesToView(
    badgeList,
    listing,
    badgeEventSubscriber
  )
  const breakpoint = hooks.useBreakpoints()

  let slidesToShow
  switch (breakpoint) {
    case 'mobile':
      slidesToShow = 1
      break
    case 'tablet':
      slidesToShow = 2
      break
    default:
      slidesToShow = 3
  }

  const carouselSettings = {
    dots: false,
    infinite: true,
    speed: 300,
    slidesToShow:
      badgesWithContent.length < slidesToShow
        ? badgesWithContent.length
        : slidesToShow,
    slidesToScroll: 1,
    fade: false,
    lazyLoad: false,
    variableWidth: false,
    autoplay: false,
    slidesPerRow: 1,
  }

  if (!badgesWithContent.length) {
    return null
  }

  return (
    <SectionPanel elmId={elmId} title='Important Information'>
      <div
        className={cx('carousel-container', {
          'full-length': badgesWithContent.length > 1,
        })}
      >
        <Carousel
          settings={carouselSettings}
          alwaysShowArrows
          elmId={`${elmId}_carousel`}
          arrowIconProps={{
            size: 'big',
          }}
        >
          {badgesWithContent.map((badge, index) => (
            <BadgeCard badge={badge} key={index} />
          ))}
        </Carousel>
      </div>
    </SectionPanel>
  )
}

export default withModalHooks(ImportantInformationSection)
